import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Chip, CircularProgress,
  IconButton, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import HistoryIcon from '@mui/icons-material/History';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import { menuProps, dialogPaperProps } from '../styles/formStyles';
import TurnoDialog from '../components/TurnoDialog/TurnoDialog';
import { turnoEditable } from '../components/TurnoDialog/useTurnoForm';

const API = process.env.REACT_APP_API_URL || '';

const ESTADO_COLORS = {
  pendiente: 'warning',
  confirmado: 'success',
  cancelado: 'error',
  demorado: 'default',
  en_recepcion: 'info',
};


const HOY = new Date().toISOString().split('T')[0];

// Tiempo de espera desde que el turno pasó a "en_recepcion" -- se recalcula solo, sin volver
// a pedirle nada al servidor (el timestamp ya vino en la fila del turno). Se re-renderiza cada
// minuto para que el texto avance mientras la pantalla queda abierta.
function TiempoEnRecepcion({ desde }) {
  const [, forzarActualizacion] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forzarActualizacion(n => n + 1), 15000);
    return () => clearInterval(id);
  }, []);
  if (!desde) return null;
  const minutos = Math.max(0, Math.floor((Date.now() - new Date(desde).getTime()) / 60000));
  const texto = minutos < 1 ? 'recién'
    : minutos < 60 ? `hace ${minutos} minuto${minutos === 1 ? '' : 's'}`
    : `hace ${Math.floor(minutos / 60)}h ${minutos % 60}min`;
  const esperaProlongada = minutos >= 20;
  return (
    <Typography sx={{ fontSize: 11, color: esperaProlongada ? '#ffb74d' : '#78909c', mt: 0.2 }}>
      🕐 {texto}
    </Typography>
  );
}

function formatFechaLabel(f) {
  const d = new Date(f + 'T12:00:00');
  return d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
}

function sumarDias(fecha, dias) {
  const d = new Date(fecha + 'T12:00:00');
  d.setDate(d.getDate() + dias);
  return d.toISOString().split('T')[0];
}

function getLunesDeSemana(fecha) {
  const d = new Date(fecha + 'T12:00:00');
  const dia = d.getDay(); // 0=dom, 1=lun...
  const diff = dia === 0 ? -6 : 1 - dia;
  d.setDate(d.getDate() + diff);
  return d.toISOString().split('T')[0];
}

function getDomingoDeSemana(lunes) {
  return sumarDias(lunes, 6);
}

function formatSemanaLabel(lunes) {
  const dom = getDomingoDeSemana(lunes);
  const dL = new Date(lunes + 'T12:00:00');
  const dD = new Date(dom + 'T12:00:00');
  return `${dL.getDate()}/${dL.getMonth()+1} — ${dD.getDate()}/${dD.getMonth()+1}`;
}

export default function Turnos() {
  const navigate = useNavigate();
  const rolActual = sessionStorage.getItem('rol') || '';
  // ── Filtros ──
  const [vistaFiltro, setVistaFiltro] = useState('dia');
  const [fechaFiltro, setFechaFiltro] = useState(HOY);
  const [medicoFiltro, setMedicoFiltro] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [modoSearch, setModoSearch] = useState(false);

  const [obrasSociales, setObrasSociales] = useState([]);
  const [turnos, setTurnos] = useState([]);
  const [medicos, setMedicos] = useState([]);
  const [disponibilidades, setDisponibilidades] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [turnoId, setTurnoId] = useState(null);
  const [turnoObj, setTurnoObj] = useState(null);
  const [dialogInitialValues, setDialogInitialValues] = useState(null);

  const [dialogModoRecibir, setDialogModoRecibir] = useState(false);
  const [turnoACancelar, setTurnoACancelar] = useState(null);
  const [cancelando, setCancelando] = useState(false);
  const [motivoCancelacionRapida, setMotivoCancelacionRapida] = useState('');
  const [errorCancelacion, setErrorCancelacion] = useState('');
  const [consultorioModal, setConsultorioModal] = useState(false);
  const [consultorioSeleccion, setConsultorioSeleccion] = useState('Consultorio 1');
  const [llamando, setLlamando] = useState(false);
  const [llamadoOkId, setLlamadoOkId] = useState(null);
  const [turnoALlamar, setTurnoALlamar] = useState(null);

  useEffect(() => {
    cargarMedicos();
    axios.get(`${API}/api/obras-sociales`).then(r => setObrasSociales(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (!modoSearch) cargarTurnos(); }, [fechaFiltro, medicoFiltro, estadoFiltro, vistaFiltro]);

  async function cargarMedicos() {
    try {
      const [medicosRes, dispRes] = await Promise.all([
        axios.get(`${API}/api/medicos`),
        axios.get(`${API}/api/medicos/disponibilidades`),
      ]);
      setMedicos(medicosRes.data || []);
      setDisponibilidades(dispRes.data || {});
    } catch (err) { console.error(err); }
  }

  async function cargarTurnos() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (vistaFiltro === 'semana') {
        const lunes = getLunesDeSemana(fechaFiltro);
        const domingo = getDomingoDeSemana(lunes);
        params.append('desde', lunes);
        params.append('hasta', domingo);
      } else {
        if (fechaFiltro) params.append('fecha', fechaFiltro);
      }
      if (medicoFiltro) params.append('medico_id', medicoFiltro);
      if (estadoFiltro) params.append('estado', estadoFiltro);
      const turnosRes = await axios.get(`${API}/api/turnos?${params.toString()}`);
      setTurnos(turnosRes.data.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function ejecutarBusqueda() {
    if (!busqueda.trim()) return;
    setModoSearch(true);
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('q', busqueda.trim());
      if (medicoFiltro) params.append('medico_id', medicoFiltro);
      if (estadoFiltro) params.append('estado', estadoFiltro);
      const r = await axios.get(`${API}/api/turnos?${params.toString()}`);
      setTurnos(r.data.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  function limpiarBusqueda() {
    setBusqueda('');
    setModoSearch(false);
    cargarTurnos();
  }

  async function cargarDatos() { await cargarTurnos(); }

  function abrirNuevo() {
    setTurnoId(null);
    setTurnoObj(null);
    setDialogInitialValues({ fecha: fechaFiltro || HOY, medico_id: medicoFiltro || '' });
    setDialogOpen(true);
  }

  function abrirEditar(t) {
    setTurnoId(t.id);
    setTurnoObj(t);
    setDialogInitialValues(null);
    setDialogOpen(true);
  }

  function abrirRecibir(t) {
    setTurnoId(t.id);
    setTurnoObj(t);
    setDialogInitialValues(null);
    setDialogModoRecibir(true);
    setDialogOpen(true);
  }

  function cerrar() {
    setDialogModoRecibir(false);
    setDialogOpen(false);
    setTurnoId(null);
    setTurnoObj(null);
    setDialogInitialValues(null);
  }

  async function llamarPaciente(turno) {
    const consultorioGuardado = sessionStorage.getItem('consultorio');
    if (!consultorioGuardado) {
      setTurnoALlamar(turno);
      setConsultorioModal(true);
      return;
    }
    await ejecutarLlamado(turno, consultorioGuardado);
  }

  async function ejecutarLlamado(turno, consultorio) {
    setLlamando(true);
    try {
      await axios.post(`${API}/api/turnero/llamar`, {
        turno_id: turno.id,
        paciente_nombre: turno.paciente_nombre,
        consultorio,
      });
      setLlamadoOkId(turno.id);
      setTimeout(() => setLlamadoOkId(null), 3000);
    } catch {}
    setLlamando(false);
  }

  async function confirmarConsultorioYLlamar() {
    sessionStorage.setItem('consultorio', consultorioSeleccion);
    axios.patch(`${API}/api/medicos/consultorio`, { consultorio: consultorioSeleccion }).catch(() => {});
    setConsultorioModal(false);
    if (turnoALlamar) {
      await ejecutarLlamado(turnoALlamar, consultorioSeleccion);
      setTurnoALlamar(null);
    }
  }

  async function confirmarCancelacion() {
    if (!turnoACancelar) return;
    setCancelando(true);
    setErrorCancelacion('');
    try {
      await axios.post(`${API}/api/turnos/${turnoACancelar.id}/cancelar`, {
        origen: 'Turnos',
        motivo: motivoCancelacionRapida || null,
      });
      setTurnoACancelar(null);
      setMotivoCancelacionRapida('');
      setErrorCancelacion('');
      cargarTurnos();
    } catch (err) {
      setErrorCancelacion(err.response?.data?.error || 'No se pudo cancelar el turno. Intentá de nuevo.');
    } finally {
      setCancelando(false);
    }
  }

  function formatFecha(f) {
    return f ? String(f).split('T')[0].split('-').reverse().join('/') : '-';
  }

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)' }}>
          Gestión de Turnos
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNuevo}
          sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' } }}>
          Nuevo Turno
        </Button>
      </Box>

      {/* ── Barra de filtros ── */}
      <Box sx={{ bgcolor: 'var(--c-superficie)', borderRadius: 2, p: 2, mb: 2, border: '1px solid var(--c-primario)', display: 'flex', flexDirection: 'column', gap: 1.5 }}>

        {/* Fila principal: todo en una línea */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>

          {/* Búsqueda */}
          <TextField
            size="small"
            placeholder="Buscar nombre o DNI..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && ejecutarBusqueda()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'var(--c-acento)', fontSize: 18 }} />
                </InputAdornment>
              ),
              style: { color: '#e0e0e0' },
            }}
            sx={{
              width: 220,
              '& .MuiOutlinedInput-root fieldset': { borderColor: 'var(--c-primario)' },
              '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'var(--c-acento)' },
              '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: 'var(--c-acento)' },
            }}
          />
          <Button
            variant="contained" size="small"
            onClick={ejecutarBusqueda}
            disabled={!busqueda.trim() || loading}
            sx={{ bgcolor: 'var(--c-acento)', color: '#000', fontWeight: 700, px: 2, height: 40,
              '&:hover': { bgcolor: 'var(--c-acento)', opacity: 0.85 },
              '&.Mui-disabled': { bgcolor: 'var(--c-primario)', color: '#555' },
            }}>
            Buscar
          </Button>
          {modoSearch && (
            <Button size="small" startIcon={<ClearIcon fontSize="small" />} onClick={limpiarBusqueda}
              sx={{ color: '#ffb74d', textTransform: 'none', fontSize: 12 }}>
              Limpiar
            </Button>
          )}

          {/* Separador vertical */}
          <Box sx={{ width: '1px', height: 32, bgcolor: 'var(--c-primario)', mx: 0.5, display: { xs: 'none', md: 'block' } }} />

          {/* Toggle Día / Semana */}
          <ToggleButtonGroup
            value={vistaFiltro} exclusive
            onChange={(_, v) => { if (v) { setVistaFiltro(v); if (modoSearch) limpiarBusqueda(); } }}
            size="small"
            sx={{
              opacity: modoSearch ? 0.45 : 1,
              '& .MuiToggleButton-root': {
                color: '#90a4ae', borderColor: 'var(--c-primario)', fontSize: 12, px: 1.5, py: 0.5,
                textTransform: 'none',
              },
              '& .MuiToggleButton-root.Mui-selected': {
                bgcolor: 'var(--c-acento)', color: '#000', fontWeight: 700,
                '&:hover': { bgcolor: 'var(--c-acento)' },
              },
            }}>
            <ToggleButton value="dia">Día</ToggleButton>
            <ToggleButton value="semana">Semana</ToggleButton>
          </ToggleButtonGroup>

          {/* Navegación de fecha */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: modoSearch ? 0.45 : 1, pointerEvents: modoSearch ? 'none' : 'auto' }}>
            <IconButton size="small" onClick={() => setFechaFiltro(f => sumarDias(f, vistaFiltro === 'semana' ? -7 : -1))} sx={{ color: 'var(--c-acento)' }}>
              <ChevronLeftIcon />
            </IconButton>
            <TextField
              type="date" size="small" value={fechaFiltro}
              onChange={e => setFechaFiltro(e.target.value)}
              inputProps={{ style: { color: '#e0e0e0', colorScheme: 'dark', fontSize: 13 } }}
              sx={{ width: 148, '& .MuiOutlinedInput-root fieldset': { borderColor: 'var(--c-primario)' } }}
            />
            <IconButton size="small" onClick={() => setFechaFiltro(f => sumarDias(f, vistaFiltro === 'semana' ? 7 : 1))} sx={{ color: 'var(--c-acento)' }}>
              <ChevronRightIcon />
            </IconButton>
            {fechaFiltro !== HOY && (
              <Button size="small" startIcon={<TodayIcon sx={{ fontSize: 16 }} />}
                onClick={() => setFechaFiltro(HOY)}
                sx={{ color: '#ffb74d', textTransform: 'none', fontSize: 12, minWidth: 0, px: 1 }}>
                Hoy
              </Button>
            )}
            <Typography sx={{ fontSize: 12, color: '#90a4ae', display: { xs: 'none', lg: 'block' } }}>
              {vistaFiltro === 'semana'
                ? formatSemanaLabel(getLunesDeSemana(fechaFiltro))
                : formatFechaLabel(fechaFiltro)}
            </Typography>
          </Box>

          {/* Médico */}
          {rolActual !== 'medico' && (
            <FormControl size="small" sx={{ minWidth: 170, opacity: modoSearch ? 0.45 : 1 }}>
              <InputLabel sx={{ color: 'var(--c-acento)', fontSize: 13 }}>Médico</InputLabel>
              <Select value={medicoFiltro} label="Médico" onChange={e => setMedicoFiltro(e.target.value)}
                sx={{ color: '#e0e0e0', fontSize: 13, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}
                MenuProps={menuProps}>
                <MenuItem value=""><em style={{ color: '#90a4ae' }}>Todos</em></MenuItem>
                {medicos.map(m => <MenuItem key={m.id} value={m.id}>{m.nombre}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          {/* Contador */}
          <Typography sx={{ fontSize: 12, color: '#90a4ae', ml: 'auto' }}>
            {!loading && `${turnos.length} turno${turnos.length !== 1 ? 's' : ''}${modoSearch ? ` · "${busqueda}"` : ''}`}
          </Typography>
        </Box>

        {/* Chips de estado */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {['', 'pendiente', 'confirmado', 'en_recepcion', 'demorado', 'atendido', 'cancelado'].map(v => (
            <Chip key={v} size="small"
              label={v === '' ? 'Todos' : v === 'en_recepcion' ? 'En recepción' : v.charAt(0).toUpperCase() + v.slice(1)}
              onClick={() => setEstadoFiltro(v)}
              sx={{
                cursor: 'pointer', fontSize: 11,
                bgcolor: estadoFiltro === v ? 'var(--c-acento)' : 'var(--c-primario)',
                color: estadoFiltro === v ? '#000' : '#e0e0e0',
                fontWeight: estadoFiltro === v ? 700 : 400,
              }} />
          ))}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <CircularProgress sx={{ color: 'var(--c-acento)' }} />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
        <TableContainer component={Paper} sx={{ bgcolor: 'var(--c-superficie)', '& .MuiTableCell-root': { color: '#e0e0e0', borderColor: 'var(--c-primario)' } }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'var(--c-primario)' }}>
                <TableCell>DNI</TableCell>
                <TableCell>Paciente</TableCell>
                <TableCell>Médico</TableCell>
                {(vistaFiltro === 'semana' || modoSearch) && <TableCell>Fecha</TableCell>}
                <TableCell>Hora</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Registrado por</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {turnos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={modoSearch || vistaFiltro === 'semana' ? 8 : 7} align="center" sx={{ color: '#b0b0b0' }}>
                    {modoSearch ? `Sin resultados para "${busqueda}"` : 'No hay turnos para esta fecha'}
                  </TableCell>
                </TableRow>
              ) : turnos.map(t => (
                <TableRow key={t.id} sx={{ '&:hover': { bgcolor: 'var(--c-primario)' } }}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{t.paciente_dni || '—'}</TableCell>
                  <TableCell>
                    {t.paciente_nombre || '—'}
                    {t.estado === 'en_recepcion' && <TiempoEnRecepcion desde={t.en_recepcion_desde} />}
                  </TableCell>
                  <TableCell>{t.medico_nombre || '—'}</TableCell>
                  {(vistaFiltro === 'semana' || modoSearch) && <TableCell>{formatFecha(t.fecha)}</TableCell>}
                  <TableCell>{String(t.hora_inicio).substring(0, 5)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip label={t.estado || 'pendiente'} color={ESTADO_COLORS[t.estado] || 'default'} size="small" />
                      {!!t.sobreturno && <Chip label="⚡ SOBRETURNO" size="small" sx={{ bgcolor: '#ff1744', color: '#fff', fontWeight: 700, fontSize: 10 }} />}
                      {!!t.especial && <Chip label="✦ ESPECIAL" size="small" title={t.motivo_especial || 'Turno especial'} sx={{ bgcolor: '#fff', color: '#1a1a1a', fontWeight: 700, fontSize: 10 }} />}
                      {t.confirmacion_estado && (
                        <Chip size="small" title="Confirmación de asistencia"
                          label={t.confirmacion_estado === 'confirmada' ? '🟢' : t.confirmacion_estado === 'rechazada' ? '🔴' : '🟡'}
                          sx={{ minWidth: 0, px: 0, '& .MuiChip-label': { px: '6px' } }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {t.creado_por
                      ? <><Typography sx={{ fontSize: 12 }}>{t.creado_por}</Typography>
                          <Typography sx={{ fontSize: 11, color: 'var(--c-acento)' }}>{formatFecha(t.created_at)}</Typography></>
                      : <Typography sx={{ color: '#666', fontSize: 12 }}>—</Typography>}
                  </TableCell>
                  <TableCell>
                    {rolActual === 'medico' && ['en_recepcion', 'demorado'].includes(t.estado) && (
                      <Button size="small"
                        startIcon={<MedicalServicesIcon sx={{ fontSize: 14 }} />}
                        onClick={() => navigate(`/consulta/${t.id}`)}
                        sx={{ color: '#81c784', minWidth: 0, px: 1, fontWeight: 700 }}
                        title="Atender paciente">
                        Atender
                      </Button>
                    )}
                    {rolActual === 'medico' && t.estado === 'atendido' && (
                      <Button size="small"
                        startIcon={<MedicalServicesIcon sx={{ fontSize: 14 }} />}
                        onClick={() => navigate(`/consulta/${t.id}`)}
                        sx={{ color: '#546e7a', minWidth: 0, px: 1, fontWeight: 700 }}
                        title="Ver evolución registrada">
                        Ver evolución
                      </Button>
                    )}
                    {rolActual === 'medico' && ['confirmado','en_recepcion'].includes(t.estado) && (
                      <Button size="small" onClick={() => llamarPaciente(t)} disabled={llamando}
                        sx={{ color: llamadoOkId === t.id ? '#66bb6a' : '#ffb74d', minWidth: 0, px: 1, fontSize: 13 }}
                        title="Llamar paciente al turnero">
                        {llamadoOkId === t.id ? '✓' : '📢'}
                      </Button>
                    )}
                    {['admin', 'recepcionista'].includes(rolActual) && ['pendiente', 'confirmado'].includes(t.estado) && (() => {
                      const editable = turnoEditable(t, rolActual);
                      return (
                        <Tooltip title={!editable ? 'Solo se pueden modificar turnos del día actual' : ''} arrow>
                          <span>
                            <Button size="small" onClick={() => abrirRecibir(t)} disabled={!editable}
                              sx={{ color: '#66bb6a', fontWeight: 700, fontSize: 11,
                                border: '1px solid rgba(102,187,106,0.35)', borderRadius: '4px', px: '8px',
                                '&:hover': { bgcolor: 'rgba(102,187,106,0.08)', borderColor: '#66bb6a' },
                                '&.Mui-disabled': { color: '#37474f', borderColor: 'rgba(102,187,106,0.1)' } }}>
                              En recepción
                            </Button>
                          </span>
                        </Tooltip>
                      );
                    })()}
                    {['admin', 'medico'].includes(rolActual) && t.paciente_id && (
                      <Button size="small"
                        startIcon={<HistoryIcon sx={{ fontSize: 14 }} />}
                        onClick={() => navigate(`/historial/${t.paciente_id}`)}
                        sx={{ color: '#ce93d8', minWidth: 0, px: 1, fontWeight: 700 }}
                        title="Ver historia clínica">
                        Historial
                      </Button>
                    )}
                    {['admin', 'recepcionista'].includes(rolActual) && !['cancelado', 'atendido'].includes(t.estado) && (() => {
                      const editable = turnoEditable(t, rolActual);
                      return (
                        <Tooltip title={!editable ? 'Solo se pueden cancelar turnos del día actual' : ''} arrow>
                          <span>
                            <Button size="small" onClick={() => setTurnoACancelar(t)} disabled={!editable}
                              sx={{ color: '#ef9a9a', minWidth: 0, px: 1, fontSize: 11,
                                '&.Mui-disabled': { color: '#37474f' } }}>
                              Cancelar
                            </Button>
                          </span>
                        </Tooltip>
                      );
                    })()}
                    <Button size="small" sx={{ color: 'var(--c-acento)' }} onClick={() => abrirEditar(t)}>Gestionar</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
      )}

      <TurnoDialog
        open={dialogOpen}
        onClose={cerrar}
        onSuccess={() => cargarTurnos()}
        turnoId={turnoId}
        turno={turnoObj}
        disponibilidades={disponibilidades}
        medicos={medicos}
        obrasSociales={obrasSociales}
        initialValues={dialogInitialValues}
        origen="Turnos"
        modoRecibir={dialogModoRecibir}
      />

      {/* ── Confirmar cancelación rápida ── */}
      <Dialog
        open={Boolean(turnoACancelar)}
        onClose={() => { setTurnoACancelar(null); setMotivoCancelacionRapida(''); setErrorCancelacion(''); }}
        maxWidth="xs" fullWidth PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, color: '#ef9a9a' }}>Cancelar turno</DialogTitle>
        <DialogContent>
          {errorCancelacion ? (
            <Box sx={{ mb: 2, p: '12px 14px', borderRadius: '8px',
              bgcolor: 'rgba(239,83,80,0.07)', border: '1px solid rgba(239,83,80,0.25)' }}>
              <Typography sx={{ fontSize: 13, color: '#ef9a9a', fontWeight: 600, mb: 0.5 }}>
                No se puede cancelar
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#ef9a9a', lineHeight: 1.5 }}>
                {errorCancelacion}
              </Typography>
            </Box>
          ) : (
            <>
              <Typography sx={{ fontSize: 14, color: '#e0e0e0', mb: 1 }}>
                El turno de <strong>{turnoACancelar?.paciente_nombre || 'este paciente'}</strong> quedará como cancelado.
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#90a4ae', mb: 2 }}>
                {turnoACancelar?.fecha ? formatFecha(turnoACancelar.fecha) : ''}{turnoACancelar?.hora_inicio ? ` · ${String(turnoACancelar.hora_inicio).substring(0, 5)}` : ''}
                {turnoACancelar?.medico_nombre ? ` · ${turnoACancelar.medico_nombre}` : ''}
              </Typography>
              <TextField
                fullWidth label="Motivo (opcional)"
                value={motivoCancelacionRapida}
                onChange={e => setMotivoCancelacionRapida(e.target.value)}
                placeholder="Ej: paciente llamó para cancelar"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } },
                  '& .MuiInputLabel-root': { color: '#90a4ae' },
                  '& .MuiInputBase-input': { color: '#e0e0e0', fontSize: 13 },
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button onClick={() => { setTurnoACancelar(null); setMotivoCancelacionRapida(''); setErrorCancelacion(''); }}
            sx={{ color: '#90a4ae' }}>
            {errorCancelacion ? 'Cerrar' : 'Volver'}
          </Button>
          {!errorCancelacion && (
            <Button variant="contained" onClick={confirmarCancelacion} disabled={cancelando}
              sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}>
              {cancelando ? 'Cancelando...' : 'Confirmar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Modal consultorio ── */}
      <Dialog open={consultorioModal} onClose={() => setConsultorioModal(false)} maxWidth="xs" fullWidth
        PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600 }}>📢 ¿Cuál es tu consultorio?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: '#90a4ae', mb: 2 }}>
            Se guardará para esta sesión. Podés cambiarlo cuando quieras.
          </Typography>
          <FormControl fullWidth>
            <InputLabel sx={{ color: 'var(--c-acento)' }}>Consultorio</InputLabel>
            <Select value={consultorioSeleccion} label="Consultorio"
              onChange={e => setConsultorioSeleccion(e.target.value)}
              sx={{ color: '#e0e0e0' }}
              MenuProps={menuProps}>
              {['Consultorio 1','Consultorio 2','Consultorio 3','Consultorio 4','Consultorio 5'].map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography sx={{ fontSize: 11, color: '#546e7a', mt: 1.5 }}>
            Consultorio actual: <strong style={{ color: '#90a4ae' }}>{sessionStorage.getItem('consultorio') || 'no seteado'}</strong>
            {sessionStorage.getItem('consultorio') && (
              <span style={{ marginLeft: 8, cursor: 'pointer', color: '#ef9a9a' }}
                onClick={() => { sessionStorage.removeItem('consultorio'); setConsultorioSeleccion('Consultorio 1'); }}>
                (cambiar)
              </span>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsultorioModal(false)} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
          <Button onClick={confirmarConsultorioYLlamar} variant="contained"
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' } }}>
            Confirmar y llamar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
